import React, { useState, useEffect, useCallback } from 'react';
import { LayoutTemplate, Save, History, Rocket, ChevronDown, ChevronUp, RotateCcw, Upload, Trash2, Plus, CircleDot, Eye, MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fj = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const imgUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);
const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2380]";

const CONFIG = {
  hero: {
    hint: 'Titolo, sottotitolo e pulsanti della prima schermata della Home',
    fields: [
      { k: 'badge', l: 'Etichetta sopra il titolo' },
      { k: 'title', l: 'Titolo principale' },
      { k: 'subtitle', l: 'Sottotitolo', t: 'textarea', rows: 3 },
      { k: 'cta1', l: 'Testo pulsante 1' },
      { k: 'cta2', l: 'Testo pulsante 2' },
    ],
  },
  chi_siamo: {
    hint: 'Società Cooperativa Società Benefit e dichiarazione per il Terzo Settore',
    fields: [
      { k: 'badge', l: 'Etichetta' },
      { k: 'title', l: 'Titolo' },
      { k: 'testo', l: 'Testo (separa i paragrafi con una riga vuota)', t: 'textarea', rows: 10 },
    ],
  },
  team: {
    hint: 'Persone, ruoli, descrizioni e foto (sostituibili quando vuoi)',
    fields: [
      { k: 'title', l: 'Titolo sezione' },
      { k: 'subtitle', l: 'Sottotitolo', t: 'textarea', rows: 3 },
    ],
    lists: [
      { k: 'delegati', l: 'Delegati Assicurativi', empty: { nome: '', ruolo: 'Delegato Assicurativo', email: '', descrizione: '', foto: '' }, item: [
        { k: 'nome', l: 'Nome' }, { k: 'ruolo', l: 'Ruolo' }, { k: 'email', l: 'Email' },
        { k: 'descrizione', l: 'Descrizione', t: 'textarea', rows: 4 }, { k: 'foto', l: 'Foto', t: 'image' },
      ]},
      { k: 'soci', l: 'Soci', empty: { nome: '', ruolo: 'Socia', foto: '' }, item: [
        { k: 'nome', l: 'Nome' }, { k: 'ruolo', l: 'Ruolo' }, { k: 'foto', l: 'Foto', t: 'image' },
      ]},
    ],
  },
  footer: {
    hint: 'Sedi e contatti del piè di pagina (i dati RUI/IVASS restano fissi per conformità)',
    fields: [
      { k: 'telefono', l: 'Telefono fisso' },
      { k: 'cellulare', l: 'Cellulare agenzia' },
      { k: 'email', l: 'Email' },
    ],
    lists: [
      { k: 'sedi', l: 'Sedi', empty: { citta: '', indirizzo: '' }, item: [
        { k: 'citta', l: 'Città' }, { k: 'indirizzo', l: 'Indirizzo' },
      ]},
    ],
  },
};

const ACTION_LABELS = { bozza: 'Bozza salvata', pubblicazione: 'Pubblicazione', ripristino: 'Ripristino' };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');

const CmsPreview = ({ sectionKey, d }) => {
  if (!d) return <p className="text-xs text-gray-400">Nessun contenuto.</p>;
  if (sectionKey === 'hero') {
    return (
      <div className="rounded-lg p-4 text-center text-white" style={{ background: 'linear-gradient(120deg,#1B2380,#0E8C84)' }}>
        <span className="inline-block text-[10px] bg-white/15 border border-white/25 rounded-full px-2.5 py-0.5 mb-2">{d.badge}</span>
        <p className="text-lg font-bold leading-snug" style={{ color: '#8FE3DA' }}>{d.title}</p>
        <p className="text-[11px] text-white/90 mt-1.5 leading-relaxed">{d.subtitle}</p>
        <div className="flex justify-center gap-2 mt-3">
          <span className="text-[10px] font-bold text-white rounded px-2.5 py-1.5" style={{ background: 'linear-gradient(90deg,#0E8C84,#0E8C84)' }}>{d.cta1}</span>
          <span className="text-[10px] font-bold text-white rounded px-2.5 py-1.5" style={{ background: 'linear-gradient(90deg,#E74C3C,#E67E22)' }}>{d.cta2}</span>
        </div>
      </div>
    );
  }
  if (sectionKey === 'chi_siamo') {
    return (
      <div className="rounded-lg p-4 bg-white border border-gray-100">
        <span className="inline-block text-[10px] font-bold text-white rounded-full px-2.5 py-0.5 mb-2" style={{ backgroundColor: '#0E8C84' }}>{d.badge}</span>
        <p className="text-base font-bold" style={{ color: '#1B2380' }}>{d.title}</p>
        <div className="space-y-1.5 mt-1.5">
          {(d.testo || '').split(/\n\s*\n/).map((p, i) => <p key={i} className="text-[11px] text-gray-600 leading-relaxed">{p}</p>)}
        </div>
      </div>
    );
  }
  if (sectionKey === 'team') {
    return (
      <div className="rounded-lg p-4 bg-white border border-gray-100">
        <p className="text-base font-bold text-center" style={{ color: '#1B2380' }}>{d.title}</p>
        <p className="text-[10px] text-gray-500 text-center mt-1 leading-relaxed">{d.subtitle}</p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {(Array.isArray(d.delegati) ? d.delegati : []).map((p, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-2 text-center">
              {p.foto ? <img src={imgUrl(p.foto)} alt={p.nome} className="w-10 h-10 rounded-lg object-cover mx-auto" /> : <div className="w-10 h-10 rounded-lg bg-[#1B2380] text-white text-xs font-bold flex items-center justify-center mx-auto">{(p.nome || '?')[0]}</div>}
              <p className="text-[11px] font-semibold mt-1 text-gray-800">{p.nome}</p>
              <p className="text-[9px] text-[#0E8C84]">{p.ruolo}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-1.5 mt-2 flex-wrap">
          {(Array.isArray(d.soci) ? d.soci : []).map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[9px] bg-gray-100 rounded-full px-2 py-0.5 text-gray-600">
              {s.foto ? <img src={imgUrl(s.foto)} alt={s.nome} className="w-3.5 h-3.5 rounded-full object-cover" /> : null}{s.nome} · {s.ruolo}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (sectionKey === 'footer') {
    return (
      <div className="rounded-lg p-4 text-white" style={{ backgroundColor: '#1a3a4a' }}>
        <p className="text-[10px] font-bold mb-1.5" style={{ color: '#8FE3DA' }}>Le Nostre Sedi</p>
        {(Array.isArray(d.sedi) ? d.sedi : []).map((s, i) => (
          <p key={i} className="text-[10px] text-white/80 flex items-center gap-1.5"><MapPin size={10} className="text-[#0E8C84]" /> <b>{s.citta}</b> — {s.indirizzo}</p>
        ))}
        <p className="text-[10px] font-bold mt-2 mb-1" style={{ color: '#8FE3DA' }}>Contatti</p>
        <p className="text-[10px] text-white/80 flex items-center gap-1.5"><Phone size={10} className="text-[#0E8C84]" /> {d.telefono} · cell. {d.cellulare}</p>
        <p className="text-[10px] text-white/80 flex items-center gap-1.5"><Mail size={10} className="text-[#0E8C84]" /> {d.email}</p>
      </div>
    );
  }
  return null;
};

const Field = ({ f, value, onChange, sectionKey, idx }) => {
  const tid = `cms-${sectionKey}${idx !== undefined ? `-${idx}` : ''}-${f.k}`;
  if (f.t === 'image') {
    const src = value && value.startsWith('/api/') ? `${API}${value}` : value;
    return (
      <div>
        <label className="block text-xs text-gray-500 mb-1">{f.l}</label>
        <div className="flex items-center gap-3">
          {src ? (
            <img src={src} alt="" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">Nessuna foto</div>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B2380] border border-[#1B2380]/30 rounded-lg px-3 py-2 cursor-pointer hover:bg-[#1B2380]/5">
            <Upload size={13} /> {src ? 'Sostituisci foto' : 'Carica foto'}
            <input data-testid={`${tid}-upload`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await fj('/api/cms/admin/images', { method: 'POST', body: fd });
                  const data = await res.json();
                  if (res.ok) { onChange(data.url); toast.success('Foto caricata: ricorda di salvare la bozza'); }
                  else toast.error(typeof data.detail === 'string' ? data.detail : 'Errore nel caricamento');
                } catch { toast.error('Errore di connessione'); }
              }} />
          </label>
          {src && (
            <button data-testid={`${tid}-remove`} type="button" onClick={() => onChange('')} className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1">
              <Trash2 size={13} /> Rimuovi
            </button>
          )}
        </div>
      </div>
    );
  }
  if (f.t === 'textarea') {
    return (
      <div>
        <label className="block text-xs text-gray-500 mb-1">{f.l}</label>
        <textarea data-testid={tid} value={value || ''} rows={f.rows || 3} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
      </div>
    );
  }
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{f.l}</label>
      <input data-testid={tid} type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </div>
  );
};

const SectionEditor = ({ section, onReload }) => {
  const cfg = CONFIG[section.key];
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(section.draft);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { setData(section.draft); }, [section.draft]);

  const setField = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setListField = (listK, idx, k, v) =>
    setData((p) => ({ ...p, [listK]: p[listK].map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await fj(`/api/cms/admin/sections/${section.key}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data }),
      });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore nel salvataggio');
    } catch { toast.error('Errore di connessione'); } finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await fj(`/api/cms/admin/sections/${section.key}/publish`, { method: 'POST' });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore nella pubblicazione');
    } catch { toast.error('Errore di connessione'); } finally { setPublishing(false); }
  };

  const loadHistory = async () => {
    if (history) { setHistory(null); return; }
    try {
      const res = await fj(`/api/cms/admin/sections/${section.key}/history`);
      const out = await res.json();
      setHistory(out.versions || []);
    } catch { toast.error('Errore di connessione'); }
  };

  const restore = async (vid) => {
    try {
      const res = await fj(`/api/cms/admin/sections/${section.key}/restore/${vid}`, { method: 'POST' });
      const out = await res.json();
      if (res.ok) { toast.success(out.message); setData(out.draft); setHistory(null); onReload(); }
      else toast.error(typeof out.detail === 'string' ? out.detail : 'Errore');
    } catch { toast.error('Errore di connessione'); }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl">
      <button data-testid={`cms-toggle-${section.key}`} type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <div>
          <p className="text-sm font-semibold text-gray-800">{section.label}</p>
          <p className="text-xs text-gray-500">{cfg.hint}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {section.has_unpublished ? (
            <span data-testid={`cms-status-${section.key}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><CircleDot size={11} /> Modifiche non pubblicate</span>
          ) : section.is_published ? (
            <span data-testid={`cms-status-${section.key}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#0E8C84]/15 text-[#0B7770]">Pubblicato {fmtDate(section.published_at)}</span>
          ) : (
            <span data-testid={`cms-status-${section.key}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Contenuto iniziale</span>
          )}
          {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
          {cfg.fields.map((f) => (
            <Field key={f.k} f={f} value={data[f.k]} onChange={(v) => setField(f.k, v)} sectionKey={section.key} />
          ))}

          {(cfg.lists || []).map((list) => (
            <div key={list.k} className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{list.l}</p>
              {(data[list.k] || []).map((item, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-semibold text-gray-600">{item.nome || item.citta || `Elemento ${idx + 1}`}</p>
                    <button data-testid={`cms-${section.key}-${list.k}-${idx}-del`} type="button"
                      onClick={() => setData((p) => ({ ...p, [list.k]: p[list.k].filter((_, i) => i !== idx) }))}
                      className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {list.item.map((f) => (
                      <div key={f.k} className={f.t === 'textarea' || f.t === 'image' ? 'sm:col-span-2' : ''}>
                        <Field f={f} value={item[f.k]} onChange={(v) => setListField(list.k, idx, f.k, v)} sectionKey={`${section.key}-${list.k}`} idx={idx} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button data-testid={`cms-${section.key}-${list.k}-add`} type="button"
                onClick={() => setData((p) => ({ ...p, [list.k]: [...(p[list.k] || []), { ...list.empty }] }))}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B2380] border border-dashed border-[#1B2380]/40 rounded-lg px-3 py-2 hover:bg-[#1B2380]/5">
                <Plus size={13} /> Aggiungi
              </button>
            </div>
          ))}

          <div className="flex flex-wrap gap-2 pt-2">
            <button data-testid={`cms-preview-${section.key}`} type="button" onClick={() => setShowPreview((p) => !p)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-gray-500 hover:text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-200 transition-colors">
              <Eye size={14} /> {showPreview ? 'Nascondi anteprima' : 'Anteprima'}
            </button>
            <button data-testid={`cms-save-${section.key}`} type="button" onClick={saveDraft} disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap border-2 border-[#1B2380] text-[#1B2380] hover:bg-[#1B2380]/5 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60">
              <Save size={14} /> {saving ? 'Salvataggio...' : 'Salva bozza'}
            </button>
            <button data-testid={`cms-publish-${section.key}`} type="button" onClick={publish} disabled={publishing}
              className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap bg-[#0E8C84] hover:bg-[#0B7770] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60">
              <Rocket size={14} /> {publishing ? 'Pubblicazione...' : 'Pubblica sul sito'}
            </button>
            <button data-testid={`cms-history-${section.key}`} type="button" onClick={loadHistory}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-gray-500 hover:text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-200 transition-colors">
              <History size={14} /> Storico
            </button>
          </div>
          <p className="text-[11px] text-gray-400">La bozza non è visibile ai visitatori: il sito cambia solo quando premi «Pubblica sul sito».</p>

          {showPreview && (
            <div className="grid md:grid-cols-2 gap-3" data-testid={`cms-preview-panel-${section.key}`}>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-amber-600 mb-1.5">Bozza — come apparirà</p>
                <CmsPreview sectionKey={section.key} d={data} />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#0B7770] mb-1.5">Pubblicato — ora online</p>
                <CmsPreview sectionKey={section.key} d={section.published} />
              </div>
            </div>
          )}

          {history && (
            <div className="space-y-1.5" data-testid={`cms-history-list-${section.key}`}>
              {history.length === 0 && <p className="text-xs text-gray-400">Nessuna versione salvata finora.</p>}
              {history.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-600">{ACTION_LABELS[v.action] || v.action} — {v.by} — {fmtDate(v.at)}</p>
                  <button data-testid={`cms-restore-${v.id}`} type="button" onClick={() => restore(v.id)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B2380] hover:underline flex-shrink-0">
                    <RotateCcw size={12} /> Ripristina nella bozza
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminContenuti() {
  const [sections, setSections] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await fj('/api/cms/admin/sections');
      if (res.ok) {
        const out = await res.json();
        setSections(out.sections || []);
      }
    } catch { /* silenzioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="pt-8 border-t border-gray-200 space-y-4">
      <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><LayoutTemplate size={18} className="text-[#1B2380]" /> Contenuti del sito</h2>
      <p className="text-sm text-gray-500">Modifica testi, foto, team e piè di pagina. Ogni sezione ha una bozza, lo storico delle versioni e il pulsante Pubblica. I numeri dei contatori si modificano nella sezione «Statistiche Impatto» qui sopra.</p>
      {sections.map((s) => <SectionEditor key={s.key} section={s} onReload={load} />)}
    </div>
  );
}
